// Shared public projects hook.
// The deployed database is the only source of truth. Bundled preview projects
// are intentionally not used as a runtime fallback.
import { useEffect, useMemo, useState } from 'react';
import {
  getPublic,
  getPublicCacheVersion,
  readPublicCache,
  toAssetUrl,
  writePublicCache,
} from '@/data/runtimeApi';

const normalizeProject = (project) => ({
  ...project,
  clientLogo: toAssetUrl(project.clientLogo),
  mainImage: toAssetUrl(project.mainImage),
  featuredLogo: toAssetUrl(project.featuredLogo),
  gallery: Array.isArray(project.gallery) ? project.gallery.map(toAssetUrl) : [],
  sections: Array.isArray(project.sections)
    ? project.sections.map((section) => ({
        ...section,
        images: Array.isArray(section.images) ? section.images.map(toAssetUrl) : [],
      }))
    : [],
});

const cachedProjects = readPublicCache('projects');
let _cache = Array.isArray(cachedProjects) ? cachedProjects : null;
let _cacheVersion = getPublicCacheVersion();
let _promise = null;

async function fetchProjects() {
  if (_promise) return _promise;

  _promise = (async () => {
    const { data } = await getPublic('/proyectos');

    if (!Array.isArray(data?.items)) {
      throw new Error('La API pública de proyectos devolvió un formato inválido.');
    }

    const projects = data.items.map(normalizeProject);
    _cache = projects;
    _cacheVersion = getPublicCacheVersion();
    writePublicCache('projects', projects);
    return projects;
  })();

  try {
    return await _promise;
  } finally {
    _promise = null;
  }
}

export function useProjects() {
  const currentCacheVersion = getPublicCacheVersion();
  if (_cacheVersion !== currentCacheVersion) {
    _cache = null;
    _cacheVersion = currentCacheVersion;
  }

  const [projects, setProjects] = useState(() => _cache || []);
  const [loading, setLoading] = useState(() => _cache === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;

    if (_cache === null) setLoading(true);
    setError(null);

    fetchProjects()
      .then((list) => {
        if (!alive) return;
        setProjects(list);
      })
      .catch((requestError) => {
        if (!alive) return;
        console.error('[public projects] No se pudieron cargar los proyectos administrados.', requestError);
        setError(requestError);
        // Keep the last successful database snapshot, if one exists. Never
        // replace it with bundled preview content.
        if (_cache) setProjects(_cache);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const helpers = useMemo(
    () => ({
      getProjectBySlug: (slug) => projects.find((project) => project.slug === slug),
      getProjectsByCategory: (category) =>
        projects.filter((project) => project.category === category),
      getFeaturedProjects: () =>
        projects.filter((project) => project.featuredOnHome === true),
      getRelatedProjects: (currentSlug, category, limit = 4) =>
        projects
          .filter(
            (project) =>
              project.category === category && project.slug !== currentSlug,
          )
          .slice(0, limit),
    }),
    [projects],
  );

  return {
    projects,
    loading,
    error,
    hasData: projects.length > 0,
    ...helpers,
  };
}

// Legacy non-reactive accessor. It now returns only the latest successful DB
// snapshot and never the bundled preview catalogue.
export function getStaticProjects() {
  return _cache || [];
}
